import { useApi } from '@backstage/core-plugin-api';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import Autocomplete, {
  createFilterOptions,
} from '@material-ui/lab/Autocomplete';
import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { useEffect, useState } from 'react';
import { customersApiRef, type Customer } from '../../api/customersApi';
import { RegisterCustomerDialog } from './RegisterCustomerDialog';

const LISTBOX_PROPS = { style: { maxHeight: 280 } };

interface RegisterOption {
  inputValue: string;
  name: string;
}

type CustomerOption = Customer | RegisterOption;

const isRegisterOption = (option: CustomerOption): option is RegisterOption =>
  'inputValue' in option;

const filterOptions = createFilterOptions<CustomerOption>();

export const CustomerPicker = ({
  formData,
  onChange,
  rawErrors,
  required,
  disabled,
  readonly,
}: FieldExtensionComponentProps<{ name: string; code: string }>) => {
  const api = useApi(customersApiRef);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>();
  const [dialogName, setDialogName] = useState<string | undefined>();

  useEffect(() => {
    api
      .listCustomers()
      .then(setCustomers)
      .catch(setError)
      .finally(() => setLoading(false));
  }, [api]);

  if (loading) {
    return (
      <Box display="flex" alignItems="center" gridGap={8}>
        <CircularProgress size={20} />
        <Typography variant="body2">Loading customers...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Typography variant="body2" color="error">
        Unable to load customers.
      </Typography>
    );
  }

  const selected =
    customers.find(customer => customer.code === formData?.code) ?? null;
  const hasError = Boolean(rawErrors?.length);

  const handleRegistered = (customer: Customer) => {
    setCustomers(current =>
      current.some(item => item.code === customer.code)
        ? current
        : [...current, { name: customer.name, code: customer.code }],
    );
    onChange({ name: customer.name, code: customer.code });
  };

  return (
    <>
      <Autocomplete<CustomerOption>
        options={customers}
        filterOptions={(options, params) => {
          const filtered = filterOptions(options, params);
          filtered.push({
            inputValue: params.inputValue,
            name: params.inputValue
              ? `Register "${params.inputValue}"...`
              : 'Register a new customer...',
          });
          return filtered;
        }}
        getOptionLabel={option => option.name}
        getOptionSelected={(option, value) =>
          !isRegisterOption(option) &&
          !isRegisterOption(value) &&
          option.code === value.code
        }
        value={selected}
        onChange={(_event, option) => {
          if (!option) return onChange(undefined);
          if (isRegisterOption(option)) return setDialogName(option.inputValue);
          return onChange({ name: option.name, code: option.code });
        }}
        disabled={disabled || readonly}
        ListboxProps={LISTBOX_PROPS}
        renderInput={params => (
          <TextField
            {...params}
            label="Customer"
            required={required}
            error={hasError}
            helperText={hasError ? rawErrors?.join(', ') : undefined}
          />
        )}
      />
      {dialogName !== undefined && (
        <RegisterCustomerDialog
          initialName={dialogName}
          onRegistered={handleRegistered}
          onClose={() => setDialogName(undefined)}
        />
      )}
    </>
  );
};
